import { BannerInlinePlaceholder } from '@tekface/smart-activation';
import { Breadcrumbs, Card, Container, Flex } from '@teko/ds-desktop';
import {
  BlockHeader,
  NoProductsFound,
  ProductCarousel,
} from '@teko/teko-ecommerce';
import { LazyComponent } from 'components/LazyComponent';
import { NotFoundProductLayout } from 'components/ProductDetail/styles';
import { INLINE_BANNER_CLASSNAME } from 'constants/smartBanner';
import { useProductDetailState, useTenantConfig } from 'contexts';
import { isServerless } from 'helpers';
import { scrollSmoothToIdWithOffset } from 'helpers/page-actions';
import { t } from 'i18n';
import { FlashSalePromotionBlock } from 'modules/product-detail/shared/FlashSalePromotionBlock';
import {
  BottomActionBar as ActionButtons,
  NewPolicies,
  OutstandingFeatures,
  ProductInfo,
  ProductPrice,
  ProductPromotions,
  RelatedPosts,
  RelatedVideos,
} from 'modules/product-detail/shared/NewLayoutComponents';
import { ReviewSection } from 'modules/product-detail/shared/ProductReview/ReviewSection';
import dynamic from 'next/dynamic';
import { Box } from 'reflexbox';
import { ProductDetailPromotion } from 'types/promotions';
import { ProductDescription } from './NewProductDescription';
import { NewProductImages, ShareAndWishlist } from './new-components';

const FAQSection = dynamic(() =>
  import('components/FAQ').then((mod) => mod.FAQSection),
);

const ConfirmOver18YearOldModal = dynamic(() =>
  import('../../shared/ConfirmOver18YearOldModal').then(
    (mod) => mod.ConfirmOver18YearOldModal,
  ),
);

const ProductCombo = dynamic(() =>
  import('./ProductCombo').then((mod) => mod.ProductCombo),
);

const PaymentPromotion = dynamic(() =>
  import('../../shared/PaymentPromotion').then((mod) => mod.PaymentPromotion),
);

const ProductVariants = dynamic(() =>
  import('../../shared/ProductVariants').then((mod) => mod.ProductVariants),
);

const RelatedPromotions = dynamic(() =>
  import('./new-components').then((mod) => mod.RelatedPromotions),
);

interface DetailPageProps {
  showRelatedPromotion?: boolean;
  extraPromotions?: ProductDetailPromotion[];
}

export const NewDetailPage: React.FC<DetailPageProps> = (props) => {
  const {
    product,
    breadcrumbs,
    inlineBanners,
    showPopupCheckAge,
    setShowPopupCheckAge,
    relatedProducts,
  } = useProductDetailState();
  const { epicOmni1550 } = useTenantConfig();
  const { showRelatedPromotion = false, extraPromotions } = props;

  if (!product) {
    return (
      <NotFoundProductLayout>
        <NoProductsFound show text={t('Không tìm thấy sản phẩm')} />
      </NotFoundProductLayout>
    );
  }

  const arrowStyle = {
    backgroundColor: 'white',
    borderRadius: 999,
    boxShadow: 'rgba(0, 0, 0, 0.2) 0px 5px 15px',
  };

  /**
   * 690: max left container width
   * 1200: max container width
   * 16 * 2: padding left and right
   */
  const leftContainerWidth = (690 / (1200 - 16 * 2)) * 100;

  return (
    <>
      <Container marginBottom="s16">
        <Breadcrumbs breadcrumbs={breadcrumbs || []} />
        <Card padding="s16">
          <Flex align="start" gap="s24">
            <Box width={`${leftContainerWidth}%`}>
              <NewProductImages />
              <ShareAndWishlist />
              <OutstandingFeatures
                onViewDetail={() =>
                  scrollSmoothToIdWithOffset('desktop-product-description')
                }
                features={product.seoInfo?.shortDescription || ''}
              />
              <ProductCombo />
              <NewPolicies />
            </Box>
            <Box width={`calc(100% - ${leftContainerWidth}% - 24px)`}>
              <FlashSalePromotionBlock />
              <Card marginBottom="s12">
                <ProductInfo />
                {(product?.productOptions?.rows || []).length > 0 && (
                  <ProductVariants />
                )}
                <ProductPrice />
                <BannerInlinePlaceholder
                  banners={inlineBanners}
                  className={INLINE_BANNER_CLASSNAME.INLINE_PDP_RELATEDPROMO}
                />
              </Card>
              <ProductPromotions />
              <ActionButtons />
              {showRelatedPromotion && (
                <LazyComponent>
                  <RelatedPromotions extraPromotions={extraPromotions} />
                </LazyComponent>
              )}
              <LazyComponent>
                <PaymentPromotion product={product} />
              </LazyComponent>
            </Box>
          </Flex>
        </Card>
        <LazyComponent>
          <Flex gap="s16" marginTop="s16" align="start">
            <Box width="68.5%">
              <Card>
                <ProductDescription
                  tabTitle={[t('Thông số kỹ thuật'), t('Chi tiết sản phẩm')]}
                  buttonType="ghost"
                />
              </Card>
            </Box>
            <Box width="calc(100% - 68.5% - 16px)">
              <Flex direction="column" gap="s8">
                <Card>
                  <RelatedPosts />
                </Card>
                <Card>
                  <RelatedVideos />
                </Card>
              </Flex>
            </Box>
          </Flex>
        </LazyComponent>
        <LazyComponent>{epicOmni1550 && <ReviewSection />}</LazyComponent>
        {!!product.seoInfo?.faq && product.seoInfo.faq.length > 0 ? (
          <LazyComponent>
            <Card marginTop="s16">
              <FAQSection
                faq={product.seoInfo.faq}
                style={{ padding: '0px 16px' }}
              />
            </Card>
          </LazyComponent>
        ) : null}
        <LazyComponent>
          {relatedProducts.length ? (
            <Card marginTop="s16">
              <BlockHeader title={t('Sản phẩm liên quan')} />
              <ProductCarousel
                products={relatedProducts}
                productsPerRow={5}
                regionName="relatedProducts"
                virtual={isServerless()}
                customStyleLeftArrow={arrowStyle}
                customStyleRightArrow={arrowStyle}
              />
            </Card>
          ) : null}
        </LazyComponent>
      </Container>

      {showPopupCheckAge && (
        <ConfirmOver18YearOldModal
          showPopupCheckAge={showPopupCheckAge}
          setShowPopupCheckAge={setShowPopupCheckAge}
          okText={t('Tôi trên 18 tuổi')}
        />
      )}
    </>
  );
};
